import type { ListBuilderConfiguration } from '../types';

/**
 * Client Ecommerce User list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_ECOMMERCE_USER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-ecommerce-users',
			cacheTTL: 600000,
			name: 'Client Ecommerce Users',
			type: 'default',
			active: true,
			defaultSortId: 'firstName-asc',
			columns: [
				{
					id: 'name',
					headers: [{ id: 'name-header', text: 'Name' }],
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'public_user',
							},
							dataConfig: {
								type: 'template',
								template: '{firstName} {lastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					minWidth: 150,
					verticalAlignment: 'top',
				},
				{
					id: 'email',
					headers: [{ id: 'email-header', text: 'Email' }],
					fields: [
						{
							id: 'email-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'email', objectTable: 'public_user' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'public_user',
							fieldName: 'email',
							direction: 'ASC',
						},
					],
					minWidth: 150,
					verticalAlignment: 'top',
				},
				{
					id: 'orderApprovalLimit',
					headers: [
						{ id: 'orderApprovalLimit-header', text: 'Order Approval Limit' },
					],
					fields: [
						{
							id: 'orderApprovalLimit-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'orderApprovalLimit',
									objectTable: 'client_public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
							formatOptions: { currencyCode: 'USD' },
						},
					],
					sortable: true,
					width: 180,
					verticalAlignment: 'top',
				},
				{
					id: 'roles',
					headers: [{ id: 'roles-header', text: 'Role' }],
					fields: [
						{
							id: 'roles-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'roleNames', objectTable: 'public_user' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								valueMapping: [
									{ value: 'Ecommerce Customer', displayText: 'Buyer' },
									{ value: 'Ecommerce Admin', displayText: 'Admin' },
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 120,
					verticalAlignment: 'top',
				},
				{
					id: 'enabled',
					headers: [{ id: 'enabled-header', text: 'Enabled' }],
					fields: [
						{
							id: 'enabled-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isEnabled',
									objectTable: 'client_public_user',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'conditional',
									conditionalRules: [
										{
											condition: 'equals',
											value: true,
											color: { type: 'theme', themeColor: 'success' },
										},
										{
											condition: 'equals',
											value: false,
											color: { type: 'theme', themeColor: 'textSecondary' },
										},
									],
								},
								valueMapping: [
									{ value: true, displayText: 'Yes' },
									{ value: false, displayText: 'No' },
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 100,
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					headers: [{ id: 'actions-header', text: '' }],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editUser',
								},
								{
									id: 'enable-action',
									label: 'Enable',
									iconName: 'check-circle',
									callbackName: 'toggleEnabled',
									conditionalVisibility: {
										field: 'isEnabled',
										condition: 'equals',
										value: false,
									},
								},
								{
									id: 'disable-action',
									label: 'Disable',
									iconName: 'ban',
									callbackName: 'toggleEnabled',
									conditionalVisibility: {
										field: 'isEnabled',
										condition: 'equals',
										value: true,
									},
								},
								{
									id: 'remove-action',
									label: 'Remove',
									iconName: 'trash',
									callbackName: 'removeUser',
									danger: true,
								},
							],
						},
					],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
					width: 140,
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'firstName-asc',
			name: 'First Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'firstName-desc',
			name: 'First Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastName-asc',
			name: 'Last Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'lastName', objectTable: 'public_user' },
			],
		},
		{
			id: 'lastName-desc',
			name: 'Last Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'email-asc',
			name: 'Email (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'email', objectTable: 'public_user' },
			],
		},
		{
			id: 'email-desc',
			name: 'Email (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'email', objectTable: 'public_user' },
			],
		},
	],
	objects: [
		{
			baseUrl: '/ecommerce-users/:id',
			objectLabel: 'Ecommerce Users',
			objectTable: 'public_user',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'firstName', fieldLabel: 'First Name' },
				{ fieldName: 'lastName', fieldLabel: 'Last Name' },
				{ fieldName: 'roleNames', fieldLabel: 'Roles' },
				{ fieldName: 'orderApprovalLimit', fieldLabel: 'Order Approval Limit' },
				{ fieldName: 'isEnabled', fieldLabel: 'Enabled' },
			],
		},
	],
};
